import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/libs';

import { AppContext } from './app-context';
import { useGetScopeDetails, useRouteScope } from './hooks';
import type { AvailableLanguages } from './types';
import { setDateLocale } from './utils';

dayjs.extend(localizedFormat);

interface AppProviderProps {
  children: ReactNode;
}

/**
 * Sole owner of the region/season/crop scope.
 *
 * The scope is derived from the route, never written to: `/region/:region/
 * season/:season` is the canonical URL and the app never operates outside it,
 * so every value exists from the first render. The topbar's selectors change
 * the scope by navigating; they do not publish it, and nothing here waits on
 * that remote bundle mounting.
 *
 * Must render inside a router and inside `<Refine>`: the scope lookups are
 * data hooks, and a hook is called on every mount regardless of whether its
 * query is enabled. Standalone mounts — stories, tests — supply both, even
 * where there is no authenticated user to fetch for.
 */
export const AppProvider = ({ children }: AppProviderProps) => {
  const { user } = useAuth();
  const { regionId: selectedRegion, seasonId: selectedCropSeason } =
    useRouteScope();
  const [selectedLanguage, setSelectedLanguage] =
    useState<AvailableLanguages>('en');

  const {
    regionName: selectedRegionName,
    cropSeasonName: selectedCropSeasonName,
    cycleCropId: selectedCycleCropId,
    hasCropSeasons,
    isLoading: isLoadingScope
  } = useGetScopeDetails({
    regionId: selectedRegion,
    seasonId: selectedCropSeason,
    // This provider renders above `AccessGuardian`, so it must hold the
    // lookups until there is a token to send; every page below the guardian
    // already has one.
    enabled: !!user
  });

  useEffect(() => {
    void setDateLocale(selectedLanguage);
  }, [selectedLanguage]);

  const value = useMemo(
    () => ({
      selectedRegion,
      selectedRegionName,
      selectedCropSeason,
      selectedCropSeasonName,
      selectedCycleCropId,
      hasCropSeasons,
      isLoadingScope,
      selectedLanguage,
      setSelectedLanguage
    }),
    [
      selectedRegion,
      selectedRegionName,
      selectedCropSeason,
      selectedCropSeasonName,
      selectedCycleCropId,
      hasCropSeasons,
      isLoadingScope,
      selectedLanguage
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
